import React from 'react';
import { css, Global } from '@emotion/react';
import { styled } from '@mui/material';
import { createDefu } from 'defu';
import videojs from "video.js";

interface VideoV2Props {
  source: {
    src: string;
    type?: string;
  }[];
  textTrack?: {
    src: string;
    lang: string;
    label?: string;
    default?: boolean;
  }[];
  options?: {
    autoplay?: boolean;
    controls?: boolean;
    poster?: string;
    playbackRates?: number[];
    // Add any other video.js options you want to support
  };
}
const DEFAULT_OPTIONS = {
  autoplay: false,
  controls: true,
  poster: "",
  playbackRates: [0.5, 1, 1.5, 2.0],
};


const VideoWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  height: 'auto',
  boxShadow: theme.shadows[1],
  '--theme-primary-color': theme.palette.primary.main,
  '--theme-secondary-color': theme.palette.secondary.main,
  '& .video-js.elice-video': {
    minWidth: '22.5rem',
    fontSize: 'var(--fluid-size)',
    fontFamily: 'Pretendard, Arial, sans-serif',
    '& .vjs-control-bar': {
      backgroundColor: 'rgba(0, 0, 0, 0.70)',
      '@media (max-width: 768px)': {
        height: '2.5em',
      },
    },
    '& button.vjs-control, & button.vjs-menu-button': {
      '& .vjs-icon-placeholder:before': {
        content: '" "',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      },
    },
    '& .vjs-progress-control.vjs-control': {
      '&>.vjs-slider': {
        margin: 0,
        '&>.vjs-play-progress': {
          backgroundColor: 'var(--theme-primary-color)',
        },
      },
      '@media (max-width: 768px)': {
        position: 'absolute',
        bottom: '2.3em',
        height: '0.5em',
        left: 0,
        width: '100%',
      },
    },
    '& .vjs-button': {
      '&.vjs-paused .vjs-icon-placeholder:before': {
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 40 40\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M15.3516 11.5234L26.6016 18.3984C27.1484 18.75 27.5 19.375 27.5 20C27.5 20.6641 27.1484 21.2891 26.6016 21.6016L15.3516 28.4766C14.7656 28.8281 14.0234 28.8672 13.4375 28.5156C12.8516 28.2031 12.5 27.5781 12.5 26.875V13.125C12.5 12.4609 12.8516 11.8359 13.4375 11.5234C14.0234 11.1719 14.7656 11.1719 15.3516 11.5234Z\' fill=\'white\'/%3E%3C/svg%3E%0A")'
      },
      '&.vjs-playing .vjs-icon-placeholder:before': {
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 40 40\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M15.625 12.5H16.875C17.8906 12.5 18.75 13.3594 18.75 14.375V25.625C18.75 26.6797 17.8906 27.5 16.875 27.5H15.625C14.5703 27.5 13.75 26.6797 13.75 25.625V14.375C13.75 13.3594 14.5703 12.5 15.625 12.5ZM23.125 12.5H24.375C25.3906 12.5 26.25 13.3594 26.25 14.375V25.625C26.25 26.6797 25.3906 27.5 24.375 27.5H23.125C22.0703 27.5 21.25 26.6797 21.25 25.625V14.375C21.25 13.3594 22.0703 12.5 23.125 12.5Z\' fill=\'white\'/%3E%3C/svg%3E%0A")'
      },
      '&.vjs-mute-control[title^="Un"] .vjs-icon-placeholder:before': {
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 40 40\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M20.5078 11.3672C20.9375 11.5625 21.25 12.0312 21.25 12.5V27.5C21.25 28.0078 20.9375 28.4375 20.5078 28.6719C20.0391 28.8672 19.5312 28.7891 19.1406 28.4375L13.8672 23.75H11.25C9.84375 23.75 8.75 22.6562 8.75 21.25V18.75C8.75 17.3828 9.84375 16.25 11.25 16.25H13.8672L19.1406 11.6016C19.5312 11.25 20.0391 11.1719 20.5078 11.3672ZM25.3516 16.5234L27.5 18.6719L29.6484 16.5234C30 16.1719 30.5859 16.1719 30.9375 16.5234C31.3281 16.9141 31.3281 17.5 30.9375 17.8516L28.7891 20L30.9375 22.1484C31.3281 22.5391 31.3281 23.125 30.9375 23.4766C30.5859 23.8672 30 23.8672 29.6484 23.4766L27.5 21.3281L25.3516 23.4766C24.9609 23.8672 24.375 23.8672 24.0234 23.4766C23.6328 23.125 23.6328 22.5391 24.0234 22.1484L26.1719 20L24.0234 17.8516C23.6328 17.5 23.6328 16.9141 24.0234 16.5234C24.375 16.1719 24.9609 16.1719 25.3516 16.5234Z\' fill=\'white\'/%3E%3C/svg%3E%0A")',
      },
      '&.vjs-mute-control .vjs-icon-placeholder:before': {
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 40 40\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M21.7578 11.3672C22.1875 11.5625 22.5 12.0312 22.5 12.5V27.5C22.5 28.0078 22.1875 28.4375 21.7578 28.6719C21.2891 28.8672 20.7812 28.7891 20.3906 28.4375L15.1172 23.75H12.5C11.0938 23.75 10 22.6562 10 21.25V18.75C10 17.3828 11.0938 16.25 12.5 16.25H15.1172L20.3906 11.6016C20.7812 11.25 21.2891 11.1719 21.7578 11.3672ZM28.4766 14.1797C30.1562 15.5859 31.25 17.6562 31.25 20C31.25 22.3828 30.1562 24.4531 28.4766 25.8203C28.0469 26.1719 27.4609 26.0938 27.1484 25.7031C26.7969 25.3125 26.875 24.7266 27.2656 24.375C28.5547 23.3594 29.375 21.7969 29.375 20C29.375 18.2422 28.5547 16.6797 27.2656 15.6641C26.875 15.3125 26.8359 14.7266 27.1484 14.3359C27.4609 13.9453 28.0469 13.8672 28.4766 14.1797ZM26.0938 17.1094C26.9531 17.8125 27.5 18.8281 27.5 20C27.5 21.2109 26.9531 22.2266 26.0938 22.9297C25.7031 23.2422 25.1172 23.2031 24.7656 22.7734C24.4531 22.3828 24.5312 21.7969 24.9219 21.4844C25.3516 21.1328 25.625 20.5859 25.625 20C25.625 19.4141 25.3516 18.9062 24.9219 18.5547C24.5312 18.2422 24.4531 17.6562 24.7656 17.2266C25.1172 16.8359 25.7031 16.7969 26.0938 17.1094Z\' fill=\'white\'/%3E%3C/svg%3E%0A")',
      },
      '&.vjs-subs-caps-button .vjs-icon-placeholder:before': {
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 40 40\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M8.75 13.75C8.75 12.3828 9.84375 11.25 11.25 11.25H28.75C30.1172 11.25 31.25 12.3828 31.25 13.75V26.25C31.25 27.6562 30.1172 28.75 28.75 28.75H11.25C9.84375 28.75 8.75 27.6562 8.75 26.25V13.75ZM16.5625 18.125C17.1094 18.125 17.6172 18.3984 17.9297 18.75C18.2812 19.1406 18.8672 19.1797 19.2578 18.8281C19.6484 18.5156 19.6875 17.8906 19.3359 17.5C18.6719 16.7578 17.6562 16.25 16.5625 16.25C14.4922 16.25 12.8125 17.9297 12.8125 20C12.8125 22.0703 14.4922 23.75 16.5625 23.75C17.6562 23.75 18.6719 23.2812 19.3359 22.5C19.6875 22.1484 19.6484 21.5234 19.2578 21.2109C18.8672 20.8594 18.2812 20.8984 17.9297 21.25C17.6172 21.6406 17.1094 21.875 16.5625 21.875C15.5078 21.875 14.6875 21.0547 14.6875 20C14.6875 18.9844 15.5078 18.125 16.5625 18.125ZM22.1875 20C22.1875 18.9844 23.0078 18.125 24.0625 18.125C24.6094 18.125 25.1172 18.3984 25.4297 18.75C25.7812 19.1406 26.3672 19.1797 26.7578 18.8281C27.1484 18.5156 27.1875 17.8906 26.8359 17.5C26.1719 16.7578 25.1562 16.25 24.0625 16.25C21.9922 16.25 20.3125 17.9297 20.3125 20C20.3125 22.0703 21.9922 23.75 24.0625 23.75C25.1562 23.75 26.1719 23.2812 26.8359 22.5C27.1875 22.1484 27.1484 21.5234 26.7578 21.2109C26.3672 20.8594 25.7812 20.8984 25.4297 21.25C25.1172 21.6406 24.6094 21.875 24.0625 21.875C23.0078 21.875 22.1875 21.0547 22.1875 20Z\' fill=\'white\'/%3E%3C/svg%3E%0A")',
      },
      '&.vjs-subs-caps-button:has(> .vjs-menu .vjs-menu-item:not(.vjs-subtitles-menu-item)[aria-checked="true"]) .vjs-icon-placeholder:before': {
        opacity: 0.5,
      },
      '&.vjs-fullscreen-control .vjs-icon-placeholder:before': {
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 40 40\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M12.5 11.25H16.25C16.9141 11.25 17.5 11.8359 17.5 12.5C17.5 13.2031 16.9141 13.75 16.25 13.75H13.75V16.25C13.75 16.9531 13.1641 17.5 12.5 17.5C11.7969 17.5 11.25 16.9531 11.25 16.25V12.5C11.25 11.8359 11.7969 11.25 12.5 11.25ZM13.75 23.75V26.25H16.25C16.9141 26.25 17.5 26.8359 17.5 27.5C17.5 28.2031 16.9141 28.75 16.25 28.75H12.5C11.7969 28.75 11.25 28.2031 11.25 27.5V23.75C11.25 23.0859 11.7969 22.5 12.5 22.5C13.1641 22.5 13.75 23.0859 13.75 23.75ZM23.75 11.25H27.5C28.1641 11.25 28.75 11.8359 28.75 12.5V16.25C28.75 16.9531 28.1641 17.5 27.5 17.5C26.7969 17.5 26.25 16.9531 26.25 16.25V13.75H23.75C23.0469 13.75 22.5 13.2031 22.5 12.5C22.5 11.8359 23.0469 11.25 23.75 11.25ZM28.75 23.75V27.5C28.75 28.2031 28.1641 28.75 27.5 28.75H23.75C23.0469 28.75 22.5 28.2031 22.5 27.5C22.5 26.8359 23.0469 26.25 23.75 26.25H26.25V23.75C26.25 23.0859 26.7969 22.5 27.5 22.5C28.1641 22.5 28.75 23.0859 28.75 23.75Z\' fill=\'white\'/%3E%3C/svg%3E%0A")',
      },
      '&.vjs-fullscreen-control[title^="Exit"] .vjs-icon-placeholder:before': {
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 40 40\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M17.5 12.5V16.25C17.5 16.9531 16.9141 17.5 16.25 17.5H12.5C11.7969 17.5 11.25 16.9531 11.25 16.25C11.25 15.5859 11.7969 15 12.5 15H15V12.5C15 11.8359 15.5469 11.25 16.25 11.25C16.9141 11.25 17.5 11.8359 17.5 12.5ZM12.5 22.5H16.25C16.9141 22.5 17.5 23.0859 17.5 23.75V27.5C17.5 28.2031 16.9141 28.75 16.25 28.75C15.5469 28.75 15 28.2031 15 27.5V25H12.5C11.7969 25 11.25 24.4531 11.25 23.75C11.25 23.0859 11.7969 22.5 12.5 22.5ZM25 12.5V15H27.5C28.1641 15 28.75 15.5859 28.75 16.25C28.75 16.9531 28.1641 17.5 27.5 17.5H23.75C23.0469 17.5 22.5 16.9531 22.5 16.25V12.5C22.5 11.8359 23.0469 11.25 23.75 11.25C24.4141 11.25 25 11.8359 25 12.5ZM23.75 22.5H27.5C28.1641 22.5 28.75 23.0859 28.75 23.75C28.75 24.4531 28.1641 25 27.5 25H25V27.5C25 28.2031 24.4141 28.75 23.75 28.75C23.0469 28.75 22.5 28.2031 22.5 27.5V23.75C22.5 23.0859 23.0469 22.5 23.75 22.5Z\' fill=\'white\'/%3E%3C/svg%3E%0A")',
      },
    },
    '& .vjs-big-play-button': {
      width: 'calc(var(--fluid-size) * 4)',
      height: 'calc(var(--fluid-size) * 4)',
      marginTop: 'calc(var(--fluid-size) * -2)',
      marginLeft: 'calc(var(--fluid-size) * -2)',
      backgroundColor: 'transparent',
      '&:before': {
        width: '100%',
        height: '100%',
        content: '""',
        backgroundColor: 'var(--theme-secondary-color)',
        opacity: 0.4,
        borderRadius: '0.3em',
        position: 'absolute',
        top: 0,
        left: 0,
      },
      '& .vjs-icon-placeholder:before': {
        fontSize: 'calc(var(--fluid-size) * 2)',
        lineHeight: 'calc(var(--fluid-size) * 3)',
      }
    },
    '& .vjs-volume-bar': {
      margin: '1.35em 0 1.35em',
      '@media (max-width: 768px)': {
        margin: '1.1em 0 1.1em',
        width: '3em',
      },
    },
    '& .vjs-time-tooltip': {
      fontSize: 'calc(var(--fluid-size) * 0.8)',
    },
    '& .vjs-control.vjs-button': {
      width: '3em',
      padding: '0 0.5em',
      '&>*::before': {
        fontSize: 'calc(var(--fluid-size) * 1.5)',
        lineHeight: 'calc(var(--fluid-size) * 3)',
        '@media (max-width: 768px)': {
          lineHeight: 'calc(var(--fluid-size) * 2.5)',
        },
      },
      '@media (max-width: 768px)': {
        width: '2em',
        padding: '0 0.2em',
      },
    },
    '& .vjs-control': {
      width: '3em',
    },
    '& .vjs-volume-panel.vjs-volume-panel-horizontal': {
      width: '8em',
      '@media (max-width: 768px)': {
        width: '6em',
      },
      '& .vjs-control.vjs-volume-control': {
        opacity: 1,
        width: '5em',
        '@media (max-width: 768px)': {
          display: 'block',
          width: 'fit-content',
          marginRight: '1em',
          height: '2.5em',
          opacity: 1,
        },
      },
    },
    '& .vjs-time-control': {
      padding: '0 0.2em',
      '&.vjs-current-time': {
        marginLeft: '0.5em',
      },
      '&.vjs-duration': {
        marginRight: '0.5em',
      },
      fontSize: 'calc(var(--fluid-size) * 0.8)',
      lineHeight: 'calc(var(--fluid-size) * 3)',
      '@media (max-width: 768px)': {
        lineHeight: 'calc(var(--fluid-size) * 2.5)',
      },
    },
    '& .vjs-playback-rate': {
      display: 'block',
      marginLeft: 'auto',
      '& .vjs-menu': {
        left: '-0.5em',
      },
      '& .vjs-playback-rate-value': {
        fontSize: 'calc(var(--fluid-size) * 0.8)',
        lineHeight: 'calc(var(--fluid-size) * 3)',
        '@media (max-width: 768px)': {
          lineHeight: 'calc(var(--fluid-size) * 2.5)',
        },
      },
      '& .vjs-menu-item': {
        fontSize: 'calc(var(--fluid-size) * 1)',
        lineHeight: 'calc(var(--fluid-size) * 1.4)',
      },
    },
    '& .vjs-menu.vjs-lock-showing .vjs-menu-item': {
      marginBlock: 0,
    },
    '& .vjs-current-time, & .vjs-duration, & .vjs-time-divider': {
      display: 'block',
      fontFamily: 'Pretendard, Arial, sans-serif',
    },
    '& .vjs-time-divider': {
      width: 'auto',
      minWidth: 0,
      padding: 0,
    },
    '& .vjs-remaining-time': {
      display: 'none',
    },
    '& .vjs-volume-level': {
      backgroundColor: 'var(--theme-primary-color)',
      color: 'var(--theme-primary-color)',
    },
    '& .vjs-load-progress': {
      backgroundColor: 'var(--theme-secondary-color)',
      opacity: 0.5,
    },
    '& .vjs-subs-caps-button .vjs-menu': {
      width: '10em',
      left: '-5em',
      '& .vjs-texttrack-settings': {
        order: 10,
      },
      '@media (max-width: 768px)': {
        left: '-2em',
        width: '6em',
      },
    },
    '& .vjs-menu-button .vjs-menu::before': {
      content: '""',
      position: 'absolute',
      bottom: '1em',
      left: 0,
      width: '100%',
      height: '0.3em',
    },
    '& .vjs-menu-content': {
      marginBottom: '0.5em',
      backgroundColor: 'rgba(0, 0, 0, 0.70)',
      borderRadius: '0.6em',
      padding: '0.4em',
      display: 'flex',
      flexDirection: 'column',
      '& > .vjs-menu-item': {
        margin: 0,
        marginBlock: 0,
        padding: '0.5em 1em',
        borderRadius: '0.4em',
        color: 'white',
        fontSize: 'calc(var(--fluid-size) * 0.8)',
        '@media (max-width: 768px)': {
          padding: '0.4em',
          fontSize: 'calc(var(--fluid-size) * 0.7)',
        },
        '&.vjs-selected': {
          backgroundColor: 'rgba(255, 255, 255, 0.50)',
        },
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.30)',
        },
      },
    },
  },
}))

const defu = createDefu((object, key, currentValue) => {
  if (Array.isArray(object[key])) {
    object[key] = currentValue;
    return true;
  }
}); defu

export function EliceVideoV2({
  source,
  textTrack,
  options: _options,
}: VideoV2Props) {
  const options = defu(_options ?? {}, DEFAULT_OPTIONS);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const playerRef = React.useRef<ReturnType<typeof videojs> | null>(null);
  return (
    <VideoWrapper>
      <Global
        styles={css`
          @import url('https://cdn.jsdelivr.net/npm/video.js@8.22.0/dist/video-js.min.css');
        `}
      />
      <video
        ref={(r: HTMLVideoElement) => {
          if (!videoRef.current) {
            videoRef.current = r;
            requestAnimationFrame(() => {
              const player = videojs(r, defu(options, {
                disablePictureInPicture: true,
                fluid: true,
                languages: {
                  ko: {
                    "Audio Player": "오디오 플레이어",
                    "Video Player": "비디오 플레이어",
                    "Play": "재생",
                    "Pause": "일시중지",
                    "Replay": "다시 재생",
                    "Current Time": "현재 시간",
                    "Duration": "지정 기간",
                    "Remaining Time": "남은 시간",
                    "Stream Type": "스트리밍 유형",
                    "LIVE": "라이브",
                    "Seek to live, currently behind live": "Seek to Live, 현재 생중계보다 뒤처짐",
                    "Seek to live, currently playing live": "Seek to Live, 현재 생중계 스트리밍 중",
                    "Loaded": "로드됨",
                    "Progress": "진행",
                    "Progress Bar": "진행 표시줄",
                    "progress bar timing: currentTime={1} duration={2}": "{2} 중 {1}",
                    "Fullscreen": "전체 화면",
                    "Exit Fullscreen": "전체 화면 해제",
                    "Mute": "음소거",
                    "Unmute": "음소거 해제",
                    "Playback Rate": "재생 속도",
                    "Subtitles": "자막",
                    "subtitles off": "자막 끄기",
                    "Captions": "자막",
                    "captions off": "자막 끄기",
                    "Chapters": "챕터",
                    "Descriptions": "제품 설명",
                    "descriptions off": "제품 설명 끄기",
                    "Audio Track": "오디오 트랙",
                    "Volume Level": "볼륨 레벨",
                    "You aborted the media playback": "비디오 재생을 취소했습니다.",
                    "A network error caused the media download to fail part-way.": "네트워크 오류로 인하여 비디오 일부를 다운로드하지 못 했습니다.",
                    "The media could not be loaded, either because the server or network failed or because the format is not supported.": "비디오를 로드할 수 없습니다. 서버 혹은 네트워크 오류 때문이거나 지원되지 않는 형식 때문일 수 있습니다.",
                    "The media playback was aborted due to a corruption problem or because the media used features your browser did not support.": "비디오 재생이 취소됐습니다. 비디오가 손상되었거나 비디오가 사용하는 기능을 브라우저에서 지원하지 않는 것 같습니다.",
                    "No compatible source was found for this media.": "비디오에 호환되지 않는 소스가 있습니다.",
                    "The media is encrypted and we do not have the keys to decrypt it.": "미디어는 암호화되어 있으며 이를 해독할 키를 갖고 있지 않습니다.",
                    "Play Video": "영상 재생",
                    "Close": "닫기",
                    "Close Modal Dialog": "대화 상자 닫기",
                    "Modal Window": "모달 창",
                    "This is a modal window": "모달 창입니다",
                    "This modal can be closed by pressing the Escape key or activating the close button.": "이 모달은 Esc 키를 누르거나 닫기 버튼을 활성화하여 닫을 수 있습니다.",
                    ", opens captions settings dialog": ", 자막 설정 대화 상자가 열립니다",
                    ", opens subtitles settings dialog": ", 자막 설정 대화 상자가 열립니다",
                    ", opens descriptions settings dialog": ", 설명 설정 대화 상자가 열립니다",
                    ", selected": ", 선택됨",
                    "captions settings": "자막 설정",
                    "subtitles settings": "자막 설정",
                    "descriptions settings": "설명 설정",
                    "Text": "텍스트",
                    "White": "화이트",
                    "Black": "블랙",
                    "Red": "레드",
                    "Green": "그린",
                    "Blue": "블루",
                    "Yellow": "옐로우",
                    "Magenta": "마젠타",
                    "Cyan": "씨엉",
                    "Background": "배경",
                    "Window": "창",
                    "Transparent": "투명",
                    "Semi-Transparent": "반투명",
                    "Opaque": "불투명",
                    "Font Size": "폰트 크기",
                    "Text Edge Style": "텍스트 가장자리 스타일",
                    "None": "없음",
                    "Raised": "글자 위치 올림",
                    "Depressed": "글자 위치 내림",
                    "Uniform": "균일",
                    "Drop shadow": "그림자 효과 넣기",
                    "Font Family": "폰트 모음",
                    "Proportional Sans-Serif": "비례 산세리프체",
                    "Monospace Sans-Serif": "고정폭 산세리프체",
                    "Proportional Serif": "비례 세리프체",
                    "Monospace Serif": "고정폭 세리프체",
                    "Casual": "캐주얼",
                    "Script": "스크립트",
                    "Small Caps": "소문자",
                    "Reset": "리셋",
                    "restore all settings to the default values": "모든 설정을 기본값으로 복원",
                    "Done": "완료",
                    "Caption Settings Dialog": "자막 설정 대화 상자",
                    "Beginning of dialog window. Escape will cancel and close the window.": "대화창 시작. Esc 키를 누르면 취소되고 창이 닫힙니다.",
                    "End of dialog window.": "대화창 종료",
                    "{1} is loading.": "{1}이(가) 로딩 중입니다.",
                    "Exit Picture-in-Picture": "Picture-in-Picture 종료",
                    "Picture-in-Picture": "Picture-in-Picture",
                    "No content": "콘텐츠 없음",
                    "Color": "색상",
                    "Opacity": "투명도",
                    "Text Background": "텍스트 배경",
                    "Caption Area Background": "자막 배경"
                  }
                }
              }));
              playerRef.current = player;
            });
            return function () {
              if (!r && playerRef.current) {
                playerRef.current?.dispose();
                playerRef.current = null;
              }
            }
          }
        }}
        className="video-js elice-video vjs-default-skin"
      >
        {source.map((srcInfo) => (
          <source key={srcInfo.src} src={srcInfo.src} type={srcInfo.type} />
        ))}
        {textTrack?.map((track) => (
          <track
            key={track.src}
            src={track.src}
            kind="subtitles"
            srcLang={track.lang}
            label={track.label}
            default={track.default}
          />
        ))}
      </video>
    </VideoWrapper>
  );
}
